import { IAllBenchmarksRequestService } from "../IAllBenchmarksRequestService";

import { BmarkFixingData } from "../../model/benchmarks/BmarkFixingData.model";
import { CubiqWorker } from "../../worker/CubiqWorker";

import { Injectable } from "@angular/core";
import { Observable, Subject } from "rxjs";
import { asyncScheduler } from "rxjs";
import { throttleTime } from "rxjs/operators";

import * as CubiqTS from "shared/cubiq-model-multi-asset";

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

const BLOTTERS_ALL_BENCHMARKS: string[] = ["All Benchmarks"];
export interface AllBenchmarksResponse extends Array<CubiqTS.OrderDataImpl> {
  /**
   * StandardReport with zero length will result in lookup deleteion
   */
  responseLength?: number;
  /**
   * AllBenchmarksResponse is in cache before it is primed.
   * StandardReport from prime may take the form of multiple responses
   * don't return it before this valid is true
   */
  primed?: boolean;
  /**
   * invalidated
   */
  invalidated?: boolean;
}

@Injectable()
export class AllBenchmarksRequestService implements IAllBenchmarksRequestService {
  private requestExtendedInfoMap = new Map<string, Map<string, string>>();

  // used in an ephemeral way
  private benchmarkOrdersSubscriptionMap: Map<string, Subject<CubiqTS.OrderDataImpl[]>> = new Map();
  private hedgeOrdersSubscriptionMap: Map<string, Subject<CubiqTS.OrderDataImpl[]>> = new Map();
  // expire after 60 seconds
  private benchmarkOrdersCacheMap: Map<string, AllBenchmarksResponse> = new Map();
  private hedgeOrdersCacheMap: Map<string, AllBenchmarksResponse> = new Map();

  private pendingIds: Set<string> = new Set();
  private internalRequestSubject = new Subject<void>();
  private primeDataProfileInProgress: Set<string> = new Set();
  private pendingInvalidationId: Set<string> = new Set();
  private recentFixingSubject: Subject<BmarkFixingData> = new Subject();
  public recentFixing$: Observable<BmarkFixingData> = this.recentFixingSubject.asObservable();

  constructor(protected cubiqWorker: CubiqWorker) {
    this.internalRequestSubject
      .pipe(throttleTime(100, asyncScheduler, { trailing: true }))
      .subscribe(() => {
        let currentIds = this.pendingIds;

        this.pendingIds = new Set<string>();

        currentIds.forEach((id) => {
          let extendedInfo = this.requestExtendedInfoMap.get(id);

          if (extendedInfo == null) {
            logger.out.error(`${this.constructor.name} id: ${id} missing extended info`);
            return;
          }

          if (
            // in both cases have subscription and cache or no subscription
            ((this.benchmarkOrdersSubscriptionMap.has(id) &&
              this.benchmarkOrdersCacheMap.has(id) &&
              this.benchmarkOrdersCacheMap.get(id).primed === true &&
              this.benchmarkOrdersCacheMap.get(id).invalidated === false) ||
              this.benchmarkOrdersSubscriptionMap.has(id) == false) &&
            ((this.hedgeOrdersSubscriptionMap.has(id) &&
              this.hedgeOrdersCacheMap.has(id) &&
              this.hedgeOrdersCacheMap.get(id).primed === true &&
              this.hedgeOrdersCacheMap.get(id).invalidated === false) ||
              this.hedgeOrdersSubscriptionMap.has(id) == false)
          ) {
            let benchmarkOrdersSubject = this.benchmarkOrdersSubscriptionMap.get(id);
            let hedgeOrdersSubject = this.hedgeOrdersSubscriptionMap.get(id);

            let clientSize =
              this.benchmarkOrdersCacheMap.get(id) != null
                ? this.benchmarkOrdersCacheMap.get(id).length
                : 0;
            let hedgeSize =
              this.hedgeOrdersCacheMap.get(id) != null
                ? this.hedgeOrdersCacheMap.get(id).length
                : 0;
            logger.out.info(
              `DEV-FIXING: CACHE-REQUEST -> [${id}] Data from CACHE  | CLIENT size: ${clientSize} | HEDGE size: ${hedgeSize} `,
            );

            if (benchmarkOrdersSubject != null)
              benchmarkOrdersSubject.next(this.benchmarkOrdersCacheMap.get(id));

            if (hedgeOrdersSubject != null)
              hedgeOrdersSubject.next(this.hedgeOrdersCacheMap.get(id));

            if (benchmarkOrdersSubject != null) this.completeBenchmarkOrders(id);
            if (hedgeOrdersSubject != null) this.completeHedgeOrders(id);

            return;
          } else if (
            this.benchmarkOrdersSubscriptionMap.has(id) &&
            this.benchmarkOrdersCacheMap.has(id) &&
            this.benchmarkOrdersCacheMap.get(id).primed === false &&
            this.hedgeOrdersSubscriptionMap.has(id) &&
            this.hedgeOrdersCacheMap.has(id) &&
            this.hedgeOrdersCacheMap.get(id).primed === false
          ) {
            logger.out.info(
              `DEV-FIXING: CACHE-REQUEST -> [${id}] primed: false dropping already in flight request`,
            );
            return;
          }

          let updateCacheFn: () => void = () => {
            let benchmarkOrders: AllBenchmarksResponse = this.clone([]);
            let hedgeOrders: AllBenchmarksResponse = this.clone([]);

            // unset expiry & invalidate old cache
            // completeBenchmarkOrders/completeHedgeOrders is now guarding
            this.invalidateById(id);
            this.benchmarkOrdersCacheMap.set(id, benchmarkOrders);
            this.hedgeOrdersCacheMap.set(id, hedgeOrders);
          };

          this.primeDataProfileInProgress.add(id);

          this.cubiqWorker
            .primeDataProfile<CubiqTS.OrderDataImpl>(
              BLOTTERS_ALL_BENCHMARKS,
              CubiqTS.OrderDataImpl.name,
              extendedInfo,
            )
            .subscribe(
              (report: CubiqTS.OrderDataImpl[]) => {
                logger.out.info(
                  `${this.constructor.name}.cubiqWorker.primeDataProfile() for id: ${id}`,
                );

                if (updateCacheFn != null) {
                  updateCacheFn();
                  updateCacheFn = null;
                }

                let benchmarkOrders = this.benchmarkOrdersCacheMap.get(id);
                let hedgeOrders = this.hedgeOrdersCacheMap.get(id);

                benchmarkOrders.push(
                  ...report.filter(AllBenchmarksRequestService.benchmarkOrdersFilterFn),
                );
                hedgeOrders.push(...report.filter(AllBenchmarksRequestService.hedgeOrdersFilterFn));

                benchmarkOrders.responseLength += report.length;
                hedgeOrders.responseLength += report.length;
              },
              (e) => {
                this.invalidateById(id);
                logger.out.error(
                  `${this.constructor.name} cubiqWorker.primeDataProfile error %O`,
                  e,
                );
              },
              () => {
                let benchmarkOrders = this.benchmarkOrdersCacheMap.get(id);
                let hedgeOrders = this.hedgeOrdersCacheMap.get(id);

                logger.out.info(
                  `DEV-FIXING: CACHE-REQUEST -> [${id}] Data from SERVER | CLIENT size: ${benchmarkOrders.length} | HEDGE size: ${hedgeOrders.length}`,
                );

                let benchmarkOrdersSubject = this.benchmarkOrdersSubscriptionMap.get(id);
                let hedgeOrdersSubject = this.hedgeOrdersSubscriptionMap.get(id);

                benchmarkOrders.primed = true;
                hedgeOrders.primed = true;

                if (benchmarkOrdersSubject != null)
                  benchmarkOrdersSubject.next(this.clone(benchmarkOrders));
                if (hedgeOrdersSubject != null) hedgeOrdersSubject.next(this.clone(hedgeOrders));
                if (benchmarkOrdersSubject != null) this.completeBenchmarkOrders(id);
                if (hedgeOrdersSubject != null) this.completeHedgeOrders(id);
                this.primeDataProfileInProgress.delete(id);
                if (this.pendingInvalidationId.has(id)) {
                  this.invalidateById(id);
                }
              },
            );
        });
      });
  }

  clone(source: AllBenchmarksResponse): AllBenchmarksResponse {
    let target: AllBenchmarksResponse = [...source];

    Object.defineProperty(target, "primed", {
      value: source.primed === true ? true : false,
      enumerable: false,
      writable: true,
    });

    Object.defineProperty(target, "invalidated", {
      value: source.invalidated === true ? true : false,
      enumerable: false,
      writable: true,
    });

    Object.defineProperty(target, "responseLength", {
      value: typeof source.responseLength === "number" ? source.responseLength : 0,
      enumerable: false,
      writable: true,
    });

    return target;
  }

  private fixingDataId(fixingData: BmarkFixingData): string {
    return fixingData.id;
  }

  private static benchmarkOrdersFilterFn(orderData) {
    return (
      orderData instanceof CubiqTS.OrderDataImpl &&
      CubiqTS.OrderType.BENCHMARK === orderData.orderType &&
      CubiqTS.BenchmarkOrderType.PositionOrder !== orderData.fixingDetails.bmOrderType
    );
  }

  private static hedgeOrdersFilterFn(orderData) {
    return (
      (orderData instanceof CubiqTS.OrderDataImpl &&
        CubiqTS.OrderType.BMALGO === orderData.orderType) ||
      (CubiqTS.OrderType.BENCHMARK === orderData.orderType &&
        CubiqTS.BenchmarkOrderType.PositionOrder === orderData.fixingDetails.bmOrderType)
    );
  }

  invalidate(fixingData: BmarkFixingData): void {
    let id = this.fixingDataId(fixingData);
    this.invalidateById(id);
  }

  invalidateById(id: string): void {
    if (
      this.benchmarkOrdersCacheMap.has(id) == false &&
      this.hedgeOrdersCacheMap.has(id) == false
    ) {
      return;
    }

    if (this.primeDataProfileInProgress.has(id)) {
      logger.out.info(
        `${this.constructor.name}.invalidateById(${id}) ignoring as primeDataProfile operation is running`,
      );
      this.pendingInvalidationId.add(id);
      return;
    }

    this.pendingInvalidationId.delete(id);

    if (this.benchmarkOrdersCacheMap.has(id)) {
      let benchmarkOrders = this.benchmarkOrdersCacheMap.get(id);
      benchmarkOrders.invalidated = true;
    }

    if (this.hedgeOrdersCacheMap.has(id)) {
      let hedgeOrders = this.hedgeOrdersCacheMap.get(id);
      hedgeOrders.invalidated = true;
    }

    this.benchmarkOrdersCacheMap.delete(id);
    this.hedgeOrdersCacheMap.delete(id);
    logger.out.info(`${this.constructor.name} invalidating cache for id ${id}`);
  }

  requestBenchmarkOrders(fixingData: BmarkFixingData): Observable<CubiqTS.OrderData[]> {
    let id = this.fixingDataId(fixingData);
    this.pendingIds.add(id);
    return this.subscribeBenchmarkOrders(fixingData);
  }

  requestHedgeOrders(fixingData: BmarkFixingData): Observable<CubiqTS.OrderData[]> {
    let id = this.fixingDataId(fixingData);
    this.pendingIds.add(id);
    return this.subscribeHedgeOrders(fixingData);
  }

  private subscribeBenchmarkOrders(fixingData: BmarkFixingData): Observable<CubiqTS.OrderData[]> {
    let extendedInfo = new Map<string, string>();
    let id = this.fixingDataId(fixingData);

    extendedInfo.set("fixingSourceCd", fixingData.code);
    extendedInfo.set("fixingDate", fixingData.fixingTimestamp.toString());
    this.requestExtendedInfoMap.set(id, extendedInfo);

    let sub: Subject<CubiqTS.OrderDataImpl[]> = null;
    if (!this.benchmarkOrdersSubscriptionMap.has(id)) {
      sub = new Subject<CubiqTS.OrderDataImpl[]>();
      this.benchmarkOrdersSubscriptionMap.set(id, sub);
    } else {
      sub = this.benchmarkOrdersSubscriptionMap.get(id);
    }

    this.internalRequestSubject.next();

    return sub.asObservable();
  }

  private subscribeHedgeOrders(fixingData: BmarkFixingData): Observable<CubiqTS.OrderData[]> {
    let extendedInfo = new Map<string, string>();
    let id = this.fixingDataId(fixingData);

    extendedInfo.set("fixingSourceCd", fixingData.code);
    extendedInfo.set("fixingDate", fixingData.fixingTimestamp.toString());
    this.requestExtendedInfoMap.set(id, extendedInfo);

    let sub: Subject<CubiqTS.OrderDataImpl[]> = null;
    if (!this.hedgeOrdersSubscriptionMap.has(id)) {
      sub = new Subject<CubiqTS.OrderDataImpl[]>();
      this.hedgeOrdersSubscriptionMap.set(id, sub);
    } else {
      sub = this.hedgeOrdersSubscriptionMap.get(id);
    }

    this.internalRequestSubject.next();

    return sub.asObservable();
  }

  private completeBenchmarkOrders(id: string): void {
    let sub = this.benchmarkOrdersSubscriptionMap.get(id);
    if (sub instanceof Subject) sub.complete();
    this.benchmarkOrdersSubscriptionMap.delete(id);
  }

  completeHedgeOrders(id: string): void {
    let sub = this.hedgeOrdersSubscriptionMap.get(id);
    if (sub instanceof Subject) sub.complete();
    this.hedgeOrdersSubscriptionMap.delete(id);
  }

  public addRecentFixing(fixingData: BmarkFixingData) {
    this.recentFixingSubject.next(fixingData);
  }
}
